import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Chip, FormControlLabel, Grid, TextField, Typography } from '@material-ui/core';
import { Content, Header, InfoCard, Link, Page, Progress, TabbedLayout, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

type Team = { name: string; members: string[]; maintainers: string[]; groups: string[] };
type User = { name: string; displayName: string; email: string; groups: string[] };
type Pending = { number: number; title: string; url: string; created: string };
type Overview = { teams: Team[]; users: User[]; pending: Pending[] };

const loginOf = (user: string) => `${user}_deuna`;
const when = (d: string) => new Date(d).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });

/** Llamada al backend `teams`; lanza el error que devuelve el backend. */
export const useTeamsCall = () => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  return useCallback(
    async (path: string, body?: unknown) => {
      const res = await fetch(`${await discovery.getBaseUrl('teams')}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error?.message ?? json.error ?? `HTTP ${res.status}`);
      return json;
    },
    [discovery, fetch],
  );
};

/** true solo para devops; undefined mientras se consulta. */
export const useTeamsAccess = () => {
  const call = useTeamsCall();
  const [allowed, setAllowed] = useState<boolean>();
  useEffect(() => {
    call('/access').then(r => setAllowed(Boolean(r.allowed)), () => setAllowed(false));
  }, [call]);
  return allowed;
};

const teamColumns: TableColumn<Team>[] = [
  { title: 'Team de GitHub', field: 'name', defaultSort: 'asc' },
  { title: 'Grupo en Backstage', field: 'groups', render: t => (t.groups.length ? t.groups.join(', ') : '—') },
  { title: 'Miembros', field: 'members', render: t => t.members.length, customSort: (a, b) => a.members.length - b.members.length },
  {
    title: 'Quiénes',
    field: 'members',
    sorting: false,
    render: t => (
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {t.members.map(m => (
          <Chip key={m} size="small" label={m.replace('_deuna', '')} />
        ))}
        {t.maintainers.map(m => (
          <Chip key={`m-${m}`} size="small" color="primary" label={`${m.replace('_deuna', '')} (maintainer)`} />
        ))}
      </span>
    ),
  },
];

/** Persona: sus teams (team_members.yaml), sus grupos en Backstage y el formulario para pedir el cambio por PR. */
const Person = ({ data, onDone }: { data: Overview; onDone: () => void }) => {
  const call = useTeamsCall();
  const logins = useMemo(
    () => [...new Set([...data.teams.flatMap(t => t.members), ...data.users.map(u => loginOf(u.name))])].sort(),
    [data],
  );
  const [login, setLogin] = useState('');
  const [wanted, setWanted] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();

  const current = useMemo(() => Object.fromEntries(data.teams.map(t => [t.name, t.members.includes(login)])), [data, login]);
  useEffect(() => setWanted(current), [current]);
  const user = data.users.find(u => loginOf(u.name) === login);
  const add = data.teams.filter(t => wanted[t.name] && !current[t.name]).map(t => t.name);
  const remove = data.teams.filter(t => !wanted[t.name] && current[t.name]).map(t => t.name);

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { url } = await call('/change', { login, add, remove, reason });
      setDone(url);
      setReason('');
      onDone();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {/* datalist nativo: busca mientras escribes sin añadir dependencias */}
        <TextField
          label="Persona (usuario de GitHub)"
          variant="outlined"
          size="small"
          inputProps={{ list: 'teams-logins' }}
          onChange={e => {
            setDone(undefined);
            setLogin(logins.includes(e.target.value) ? e.target.value : '');
          }}
          style={{ width: 420 }}
        />
        <datalist id="teams-logins">
          {logins.map(l => (
            <option key={l} value={l} />
          ))}
        </datalist>
      </Grid>
      {login && (
        <>
          <Grid item xs={12} md={5}>
            <InfoCard title={user?.displayName || login}>
              <Typography>{user?.email ?? 'Aún no está en el catálogo de Backstage (aparece tras sincronizar).'}</Typography>
              <Typography style={{ marginTop: 8 }}>
                <b>Grupos en Backstage hoy:</b> {user?.groups.join(', ') || '—'}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8, opacity: 0.7 }}>
                architects, leads y admins se mantienen a mano en el catálogo; aquí solo se cambian los teams de GitHub.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <InfoCard title="Teams de GitHub" subheader="Marca o desmarca y pide el cambio: se abre un PR en github-administration">
              {error && <WarningPanel title="No se pudo pedir el cambio" message={error} />}
              {done && (
                <Typography style={{ marginBottom: 8 }}>
                  PR abierto: <Link to={done}>{done}</Link>. Tras el merge y el apply de platform-core, usa «Sincronizar catálogo».
                </Typography>
              )}
              <Grid container>
                {data.teams.map(t => (
                  <Grid item xs={12} sm={6} key={t.name}>
                    <FormControlLabel
                      control={<Checkbox checked={Boolean(wanted[t.name])} onChange={e => setWanted({ ...wanted, [t.name]: e.target.checked })} />}
                      label={`${t.name}${t.groups.length ? ` → ${t.groups.join(', ')}` : ''}`}
                    />
                  </Grid>
                ))}
              </Grid>
              <TextField fullWidth size="small" label="Motivo (obligatorio)" value={reason} onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 300 }} />
              <Typography style={{ margin: '8px 0' }}>
                {add.length || remove.length ? `Cambios: ${[...add.map(t => `+${t}`), ...remove.map(t => `-${t}`)].join(' ')}` : 'Sin cambios'}
              </Typography>
              <Button variant="contained" color="primary" disabled={busy || !reason.trim() || add.length + remove.length === 0} onClick={submit}>
                Pedir cambio (PR)
              </Button>
            </InfoCard>
          </Grid>
        </>
      )}
    </Grid>
  );
};

/** Equipos y roles: teams de GitHub, su grupo en Backstage y cambios por PR. Solo devops. */
export const TeamsPage = () => {
  const allowed = useTeamsAccess();
  const call = useTeamsCall();
  const [data, setData] = useState<Overview>();
  const [error, setError] = useState<string>();
  const [synced, setSynced] = useState<string>();

  const load = useCallback(() => call('/overview').then(setData, e => setError(String(e?.message ?? e))), [call]);
  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const sync = () => call('/sync', {}).then(r => setSynced(r.url), e => setError(String(e?.message ?? e)));

  let body;
  if (allowed === undefined || (allowed && !data && !error)) body = <Progress />;
  else if (!allowed) body = <WarningPanel title="Sin acceso" message="Equipos y roles es solo para devops." />;
  else if (error || !data) body = <WarningPanel title="No se pudo leer los equipos" message={error} />;
  else
    body = (
      <TabbedLayout>
        <TabbedLayout.Route path="/" title="Por equipo">
          <Table<Team>
            title={`Teams de GitHub (${data.teams.length})`}
            subtitle="Fuente: deuna-iac-do-github-administration/config/teams/team_members.yaml"
            options={{ paging: false, search: true, padding: 'dense' }}
            columns={teamColumns}
            data={data.teams}
          />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/persona" title="Por persona / cambiar rol">
          <Person data={data} onDone={load} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/pendientes" title={`Pendientes (${data.pending.length})`}>
          <InfoCard title="Cambios de rol pendientes" subheader="PRs abiertos desde esta página en github-administration">
            {data.pending.length === 0 && <Typography>Ninguno.</Typography>}
            {data.pending.map(p => (
              <Typography key={p.number}>
                <Link to={p.url}>#{p.number}</Link> {p.title} · {when(p.created)}
              </Typography>
            ))}
          </InfoCard>
        </TabbedLayout.Route>
      </TabbedLayout>
    );

  return (
    <Page themeId="tool">
      <Header title="Equipos y roles" subtitle="Teams de GitHub, su rol en Backstage y cambios por PR">
        {allowed && data && (
          <Button variant="contained" color="primary" onClick={sync}>
            Sincronizar catálogo
          </Button>
        )}
      </Header>
      <Content>
        {synced && (
          <Typography style={{ marginBottom: 12 }}>
            Sincronización lanzada: <Link to={synced}>ver el workflow</Link>. Abre un PR en el catálogo que revisa el bot.
          </Typography>
        )}
        {body}
      </Content>
    </Page>
  );
};
